import React, {
	lazy,
	memo,
	Suspense,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import type { DocNode } from '@atlaskit/adf-schema';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { SmartCardContext, useSmartLinkContext } from '@atlaskit/link-provider';
import Lozenge from '@atlaskit/lozenge';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { HarmfulContentModal } from '@atlassian/ai-feedback';
import {
	isActionHidingFooterButtons,
	isSupportedStreamAction,
} from '@atlassian/conversation-assistant-action-helpers/utils';
import { ContentRenderer } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/ContentRenderer';
import { MessageRenderer } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/MessageRenderer';
import { appendContent } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { normalizeCitations } from '@atlassian/conversation-assistant-content-transformer/normalize-citations';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { shouldNotRenderLLMContentForAction } from '@atlassian/conversation-assistant-message-actions/controllers/message-actions';
import { type ActionContentProcessorFn } from '@atlassian/conversation-assistant-message-actions/controllers/message-actions-container/types';
import { ReadOnlyDefaultActionCards } from '@atlassian/conversation-assistant-message-actions/controllers/message-actions/ReadOnlyDefaultActionCards';
import { useActionRenderer } from '@atlassian/conversation-assistant-message-actions/controllers/message-actions/use-action-renderer';
import { isSharedSnapshotMessage } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type MessageAction as APIMessageAction,
	type MessageActionPayload,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import { type StoreAssistant } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useCurrentActionAuthActions } from '@atlassian/conversation-assistant-store/controllers/current-action-auth';
import { useIsMessageFeedbackSent } from '@atlassian/conversation-assistant-store/controllers/message-feedback';
import { useStoreUserMessagesPreference } from '@atlassian/conversation-assistant-store/controllers/store-user-messages-be';
import { useSelectedSpaceSelector } from '@atlassian/conversation-assistant-store/controllers/ui-store';
import { useUserPreferences } from '@atlassian/conversation-assistant-store/controllers/user-preferences';
import { Paper } from '@atlassian/conversation-assistant-ui-components/ui/Paper';
import type { LuminaInteraction } from '@atlassian/lumina/interaction';
import { AuthoritativeAnswerAttribution } from '@atlassian/rovo-platform-ui-components/authoritative-answer-attribution';
import {
	buildAuthoritativeAnswerPanelShownEvent,
	getAuthorityAttributes,
} from '@atlassian/rovo-platform-ui-components/authoritative-answer-attribution/authoritative-answer-panel-shown-event';
import {
	AUTHORITY_FEEDBACK_ORIGIN_CHAT,
	FEEDBACK_ENTRYPOINT_ID_CHAT,
	submitAuthorityFeedback,
} from '@atlassian/rovo-platform-ui-components/authoritative-answer-attribution/submit-authority-feedback';
import {
	InsightExtension,
	type ExtensionComponentProps,
} from '@atlassian/rovo-platform-ui-components/insight-extension-components';

import { getLinkCounts } from '../../../common/hooks/useLinkCounts';
import { useStoreAssistantCsatState } from '../../../common/hooks/useStoreAssistantCsatState';
import { MessageFooterButtons } from '../../../common/ui/message-footer-buttons';
import { FeedbackConfirmationBanner } from '../../../common/ui/message-footer-buttons/feedback-confirmation-banner/FeedbackConfirmationBanner';
import feedbackMessages from '../../../common/ui/message-footer-buttons/feedback-confirmation-banner/messages';
import { adfSmartlinksPlugin } from '../../../common/utils/adf-smartlinks-plugin';
import { interactionMetrics } from '../../../common/utils/browser-metrics/interactionMetrics';
import { getCurrentOrigin } from '../../../common/utils/date-time-util/getCurrentOrigin';
import { extractInsertableContent } from '../../../common/utils/extract-insertable-content';
import { useGlobalConfig } from '../../../common/utils/global-config/useGlobalConfig';
import { useUIConfig } from '../../../common/utils/ui-config/useUIConfig';
import { useCopyPasteAdopted } from '../../../common/utils/use-copy-paste-adopted/use-copy-paste-adopted';
import { useXpcLinkWrapper } from '../../../common/utils/use-xpc-link-wrapper';
import { useAgentDebugModalActions } from '../../../controllers/agent-debug-modal/useAgentDebugModalActions';
import { useMessageActionedAnalytics } from '../../../controllers/analytics/use-message-actioned-analytics';
import { getInteractionModeExecutedFromStoreAssistant } from '../../../controllers/chat-store/getInteractionModeExecutedFromStoreAssistant';
import { getMarkdownDataByType } from '../../../controllers/chat-store/getMarkdownDataByType';
import { interactionModeExecutedAttributes } from '../../../controllers/chat-store/interactionModeExecutedAttributes';
import { useChatStoreActions } from '../../../controllers/chat-store/useChatStoreActions';
import { useConversation } from '../../../controllers/conversation/useConversation';
import { useDataSculptorModalActions } from '../../../controllers/data-sculptor-modal/useDataSculptorModalActions';
import { useFeedbackV2 } from '../../../controllers/feedback-v2';
import { useFeedbackTrigger } from '../../../controllers/feedback-v2/use-feedback-trigger';
import { useMessageHistory } from '../../../controllers/message-history';
import { usePluginInvocationsHandler } from '../../../controllers/plugin-invocations';
import { ConversationAssistantUIModes } from '../../../types';
import { RedactionEnforcementMessage } from '../store-human/redacted-prompt/RedactionEnforcementMessage';

import { AgentResponseHat } from './agent-response-hat';
import { buildClipboardPayload } from './copy-response/buildClipboardPayload';
import { injectRedactScribbles } from './injectRedactScribbles';
import { getLuminaAnalyticsInteractionType } from './lumina/lumina-interaction-analytics';
import { MessageActions } from './message-actions';
import { MessageStreamActions } from './message-stream-actions/MessageStreamActions';
import messages from './messages';
import { RedactScribbleExtension } from './RedactScribbleExtension';
import { type ResponseAuthorIdentity } from './ResponseAuthorIdentity';
import { useResponseHatState } from './useResponseHatState';

const CsatSurvey = lazy(() =>
	import(
		/* webpackChunkName: "csat-survey" */
		'../../../common/ui/message-footer-buttons/csat-survey/CsatSurvey'
	).then((m) => ({
		default: m.CsatSurvey,
	})),
);

const CONFLUENCE_SUGGESTION_KEY = 'Confluence-Suggestion-Plugin';
const CODE_SEARCH_PLUGIN_KEY = 'Code-Search-Plugin';
const ROVO_SPACES_DETAILED_FEEDBACK_SOURCE = 'rovo-spaces-v2-detailed';

const canWriteRichTextToClipboard = (html: string | null | undefined): html is string =>
	!!html &&
	typeof ClipboardItem !== 'undefined' &&
	typeof navigator?.clipboard?.write === 'function';

const writeRichTextToClipboard = async (html: string, plain: string): Promise<void> => {
	await navigator.clipboard.write([
		new ClipboardItem({
			'text/html': new Blob([html], { type: 'text/html' }),
			'text/plain': new Blob([plain], { type: 'text/plain' }),
		}),
	]);
};

const getRovoChatBridgeProduct = (
	mode: ConversationAssistantUIModes | undefined,
	isFullScreen: boolean | undefined,
): string =>
	isFullScreen
		? 'rovo-chat.fullscreen'
		: `rovo-chat.${mode ?? ConversationAssistantUIModes.SIDEBAR}`;

type RovoXpcSmartCardProviderProps = {
	mode?: ConversationAssistantUIModes;
	fullScreen?: boolean;
	children: React.ReactNode;
};

/**
 * Wraps children in a SmartCardContext override that forces the Rovo-chat XPC
 * bridge/product onto the smart-card context for the MessageRenderer/markdown path,
 * including inline Lumina content.
 *
 * The bridge is computed synchronously from `mode`/`fullScreen` props, so it is
 * correct on the FIRST render. This avoids the transient `smartLinks` fallback
 * that only reproduces in optimized (branch-deploy) builds — most notably for the
 * mini-modal, where the MessageRenderer path previously had no XPC override and
 * its smart cards inherited a context without a Rovo-chat bridge.
 *
 * Uses the raw `SmartCardContext.Provider` (not `<SmartCardProvider>`) so it does
 * NOT depend on link-provider's `parentContext || providerValue` merge behavior —
 * the explicit override always wins, regardless of mount/render order.
 */
const RovoXpcSmartCardProvider = ({
	mode,
	fullScreen,
	children,
}: RovoXpcSmartCardProviderProps): React.JSX.Element => {
	const smartLinkContext = useSmartLinkContext();
	const { productKey } = useGlobalConfig();

	const bridgeProduct = getRovoChatBridgeProduct(mode, fullScreen);
	const xpcProduct = smartLinkContext.xpcProduct || productKey;
	const { xpcSubProduct } = smartLinkContext;

	const smartCardContextValue = useMemo(
		() => ({
			...smartLinkContext,
			bridgeProduct,
			xpcProduct,
			xpcSubProduct,
		}),
		[smartLinkContext, bridgeProduct, xpcProduct, xpcSubProduct],
	);

	// Remount the card subtree if the bridge identity changes (e.g. mode flips
	// from sidebar to mini-modal), so any URL wrapped against a stale bridge is
	// re-wrapped against the correct one.
	const smartCardIdentityKey = [bridgeProduct, xpcProduct ?? '', xpcSubProduct ?? ''].join(':');

	return (
		<SmartCardContext.Provider key={smartCardIdentityKey} value={smartCardContextValue}>
			{children}
		</SmartCardContext.Provider>
	);
};

const writePlainTextToClipboard = async (text: string): Promise<void> => {
	if (typeof navigator?.clipboard?.writeText !== 'function') {
		return;
	}
	await navigator.clipboard.writeText(text);
};

type ConfluenceSuggestionAction = APIMessageAction & {
	key: 'Confluence-Suggestion-Plugin';
	data: {
		content: string;
	};
};

type StoreAssistantViewProps = {
	storeAssistant: StoreAssistant;
	previousAssistantAuthor?: ResponseAuthorIdentity;
	isConversationActivelyGenerating?: boolean;
	/**
	 * Whether the "{agent} is thinking" indicator has handed off for this message. The
	 * response hat is shown only once this is true, so it directly succeeds the thinking
	 * indicator without a gap or overlap. Defaults to `true` (no live thinking indicator).
	 */
	thinkingIndicatorHasHandedOff?: boolean;
	/** Suppresses the internal hat when ChatHistory owns the active streaming handoff slot. */
	suppressInternalResponseHat?: boolean;
	onAuthLinkClicked?: (authType: string) => void;
	/**
	 * Invoked when an inline actionable follow-up question (parsed from the `◈`
	 * marker) is clicked. Forwarded to the message renderer.
	 */
	onFollowUpClick?: (question: string) => void;
	interactionMode?: 'live' | 'read-only';
	mode?: ConversationAssistantUIModes;
	product?: string;
	isLastMessage?: boolean;
	isLastActionMessage?: boolean;
};

const styles = cssMap({
	responseCard: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	responseContent: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
	},
	legacyActionsWrapper: {
		paddingBlockStart: token('space.100'),
	},
	snapshotActionPlaceholder: {
		display: 'inline-flex',
		paddingBlockStart: token('space.075'),
		paddingInlineEnd: token('space.150'),
		paddingBlockEnd: token('space.075'),
		paddingInlineStart: token('space.150'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	betaLabel: {
		flexShrink: 0,
		marginInlineEnd: token('space.100'),
	},
	betaLabelWrapper: {
		borderRadius: token('radius.large', '6px'),
		marginBlockStart: token('space.200'),
		display: 'flex',
		alignItems: 'center',
		flexShrink: 1,
	},
});

const BetaMessageLabel = memo(() => {
	const { formatMessage } = useIntl();

	return (
		<Box
			backgroundColor="color.background.neutral"
			paddingBlock="space.100"
			paddingInline="space.200"
			xcss={styles.betaLabelWrapper}
		>
			<Box xcss={styles.betaLabel}>
				<Lozenge appearance="discovery">{formatMessage(messages.betaLozenge)}</Lozenge>
			</Box>
			{formatMessage(messages.betaFeaturesMessage)}
		</Box>
	);
});

const InsightExtensionWithLayoutContext = (props: ExtensionComponentProps) => {
	const { fullScreen } = useUIConfig();

	return <InsightExtension {...props} isFullScreen={Boolean(fullScreen)} />;
};

export const StoreAssistantView: React.MemoExoticComponent<
	(props: StoreAssistantViewProps) => React.JSX.Element | null
> = memo(
	({
		storeAssistant,
		previousAssistantAuthor,
		isConversationActivelyGenerating = false,
		thinkingIndicatorHasHandedOff = true,
		suppressInternalResponseHat = false,
		onAuthLinkClicked,
		onFollowUpClick,
		interactionMode,
		mode,
		product,
		isLastMessage,
		isLastActionMessage,
	}: StoreAssistantViewProps): React.JSX.Element | null => {
		di(
			useIsMessageFeedbackSent,
			fg,
			useConversation,
			useResponseHatState,
			useSelectedSpaceSelector,
			useFeedbackV2,
		);
		const { formatMessage } = useIntl();
		const { elements, customHandlers, isContentPreviewSupported, fullScreen } = useUIConfig();

		// TODO: implement getters for these values
		const {
			content: { message: assistantResponse, streamingComplete = true, type: messageType },
			invokedActions,
			markdownData: storeMarkdownData,
		} = storeAssistant;

		const {
			message: {
				content,
				conversation_channel_id: conversationId,
				actions,
				id: agentMessageId,
				sources,
				plugin_invocations,
				jira_activation_id: jiraActivationId,
				insertable_sections: insertableSections,
				authority_ui_metadata: topLevelAuthorityUiMetadata,
				message_metadata: messageMetadata,
			},
		} = assistantResponse;

		// authority_ui_metadata is at the top level for streaming responses, but
		// nested under message_metadata for history responses.
		const authorityUiMetadata =
			topLevelAuthorityUiMetadata ?? messageMetadata?.authority_ui_metadata;

		if (fg('rovo_chat_jira_nl_to_jql_minion_in_fe')) {
			//eslint-disable-next-line react-hooks/rules-of-hooks
			usePluginInvocationsHandler(plugin_invocations);
		}

		const {
			profileData: { aaid, name, email },
			feedbackCollector: { entryPointId },
			productKey,
			siteId,
			orgId,
			emojiProvider,
			assistanceServiceConfig,
			experienceId,
			productHeader,
		} = useGlobalConfig();
		const { setAuth } = useCurrentActionAuthActions();
		const { conversation } = useConversation();
		const [{ selectedSpace }] = useSelectedSpaceSelector();
		const feedbackTrigger = useFeedbackTrigger();
		const xpcWrapUrl = useXpcLinkWrapper();
		const detailedFeedbackSource =
			FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false) &&
			(selectedSpace?.id || conversation?.rovoSpaceId)
				? ROVO_SPACES_DETAILED_FEEDBACK_SOURCE
				: undefined;

		const { openModal: openDataSculptorModal } = useDataSculptorModalActions();
		const { openModal: openAgentDebugModal } = useAgentDebugModalActions();
		const renderedContentRef = React.useRef<HTMLDivElement>(null);

		/**
		 * ⚠️ Technical debt: Confluence suggestion actions are currently handled in a special way.
		 * it was built as an action, and actions are intended to render with their own components or
		 * default to a Confirm/Cancel button, all of which will hit the action execution endpoint
		 * to keep the BE service updated on what happens.
		 *
		 * This has since changed to simply prefering the InsertReplaceButton to handle the confluence
		 * suggested content, and it does not need to phone home when actioned.
		 *
		 * We assume that when Confluence-Suggestion-Plugin is used, it will be the only action in the array.
		 * Reference: https://atlassian.slack.com/archives/C06PKQPN8BF/p1723595459892209?thread_ts=1723419814.967079&cid=C06PKQPN8BF
		 */
		const { filteredActions, insertReplaceContent, suggestionContent, plainTextClipboardContent } =
			useMemo(() => {
				const confluenceEditorAction = actions?.find(
					(action) => action.key === CONFLUENCE_SUGGESTION_KEY,
				) as ConfluenceSuggestionAction | undefined;
				const filteredActions = actions?.filter(
					(action) => action.key !== CONFLUENCE_SUGGESTION_KEY,
				);
				const suggestionContent = confluenceEditorAction?.data.content ?? '';
				let insertReplaceContent: string | DocNode | undefined =
					confluenceEditorAction?.data.content ?? content;
				let plainTextClipboardContent = content?.toString();

				// When the BE provides insertable_sections indices, slice only those
				// portions of the content for the "Insert to page" button.
				// Falls back to the full content when insertable_sections is absent or empty.
				const insertSectionsEnabled = expValEquals(
					'cc_ai_insert_to_page_rovo_chat_experiment',
					'isEnabled',
					true,
				);
				if (insertSectionsEnabled && !confluenceEditorAction && typeof content === 'string') {
					insertReplaceContent = extractInsertableContent(content, insertableSections);
				}

				// Append sources to clipboard content
				if (sources) {
					const { result: contentResult, sourcesString: contentSourcesString } = normalizeCitations(
						content?.toString() ?? '',
						sources,
					);

					plainTextClipboardContent = `${contentResult}\n\n${formatMessage(
						messages.sourcesTitle,
					)}\n\n${contentSourcesString}`;
				}

				return {
					filteredActions,
					insertReplaceContent,
					suggestionContent,
					plainTextClipboardContent,
				};
			}, [actions, content, sources, formatMessage, insertableSections]);

		const hasAnswerContent = Boolean(content?.toString().trim());

		const hasAutoInsertedContent = useMemo(() => {
			return filteredActions?.some((action) => isSupportedStreamAction({ key: action.key }));
		}, [filteredActions]);

		// Hide thumbs up/down & copy footer for ask-question, plan-complete, or
		// tool-source actions rendered as a permission confirmation block.
		const shouldHideFooterButtons = useMemo(() => {
			return (
				filteredActions?.some((action) => isActionHidingFooterButtons({ key: action.key })) ||
				(!!filteredActions?.length &&
					filteredActions.every((action) => Boolean(action.toolSource))) ||
				// Mirror pre-stream stop behavior for cancelled page-stream turns.
				(conversation?.lastAgentMessageStatus === 'CANCELLED' &&
					hasAutoInsertedContent &&
					expValEqualsNoExposure('platform_editor_remove_pause_streaming', 'isEnabled', true))
			);
		}, [conversation?.lastAgentMessageStatus, hasAutoInsertedContent, filteredActions]);
		const shouldRenderLLMContent: boolean = useMemo(() => {
			return !filteredActions?.some(shouldNotRenderLLMContentForAction);
		}, [filteredActions]);

		const assistantResponseMessage = useMemo(() => {
			if (!suggestionContent?.length) {
				return assistantResponse.message;
			}

			return {
				...assistantResponse.message,
				content: appendContent(content, `---\n\n ${suggestionContent}`),
			};
		}, [suggestionContent, assistantResponse.message, content]);

		const shouldShowBetaLabel = useMemo(() => {
			return assistantResponse.message.plugin_invocations?.some((plugin) => {
				return plugin.plugin_name === CODE_SEARCH_PLUGIN_KEY;
			});
		}, [assistantResponse.message.plugin_invocations]);

		const { selectedAgent } = useConversation({
			conversationId,
		});
		const shouldEnableAgentResponseHat = isExperimentEnabled('rovo_chat_mention_agents');
		const assistanceServiceParams = useMemo(
			() => ({
				baseUrl: assistanceServiceConfig?.baseUrl,
				headers: assistanceServiceConfig?.headers,
				channelId: assistanceServiceConfig?.channelId,
				experienceId,
				product: productHeader,
				siteId,
				touchpointSource: productHeader,
			}),
			[
				assistanceServiceConfig?.baseUrl,
				assistanceServiceConfig?.headers,
				assistanceServiceConfig?.channelId,
				experienceId,
				productHeader,
				siteId,
			],
		);
		const { resolveConversationAction } = useChatStoreActions();

		const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();

		const messageActivelyStreaming = useMemo(() => {
			return messageType === 'FINAL_RESPONSE' && !streamingComplete;
		}, [streamingComplete, messageType]);

		const {
			shouldRender: shouldRenderAgentResponseHatFromState,
			isDefaultRovo: isResponseAuthorDefaultRovo,
			identityAccountId: responseHatAgentIdentityAccountId,
			isHydrating: isResponseAuthorIdentityHydrating,
		} = useResponseHatState({
			storeAssistant,
			previousAssistantAuthor,
			shouldEnableAgentResponseHat,
			canRenderAfterThinking: Boolean(thinkingIndicatorHasHandedOff),
			isConversationActivelyGenerating: Boolean(isConversationActivelyGenerating),
			hasAnswerContent,
			assistanceServiceParams,
		});
		const shouldRenderAgentResponseHat =
			shouldRenderAgentResponseHatFromState && !suppressInternalResponseHat;

		useLayoutEffect(() => {
			if (messageActivelyStreaming) {
				interactionMetrics.timeToRenderFinalResponse.start();
			} else {
				interactionMetrics.timeToRenderFinalResponse.stop();
			}
		}, [messageActivelyStreaming]);

		useIsMessageFeedbackSent(agentMessageId);

		const {
			preferences: { hasSeenFeedbackOptInPrompt },
			setHasSeenFeedbackOptInPrompt,
		} = useUserPreferences();
		const { storeUserMessages, setStoreUserMessages } = useStoreUserMessagesPreference({
			assistanceServiceParams,
		});

		const [isHarmfulContentModalOpen, setIsHarmfulContentModalOpen] = useState(false);
		const [, setIsAupFeedbackSent] = useState(false);

		const analyticsMessageActions = useMemo(() => {
			return assistantResponse.message.actions?.map((action) => action.key);
		}, [assistantResponse.message.actions]);

		const interactionModeExecuted = getInteractionModeExecutedFromStoreAssistant(storeAssistant);
		const onLuminaInteraction = useCallback(
			(interaction: LuminaInteraction) => {
				if (!fg('rovo_chat_lumina_component_interaction_tracking')) {
					return;
				}
				const luminaInteractionType = getLuminaAnalyticsInteractionType(interaction);
				if (!luminaInteractionType) {
					return;
				}
				trackAIMAUEvent({
					type: 'actioned',
					actionResult: 'luminaComponentInteracted',
					proactive: false,
					messageId: agentMessageId,
					messageActions: analyticsMessageActions,
					singleInstrumentationID: agentMessageId,
					attributes: {
						luminaInteractionType,
						...interactionModeExecutedAttributes(interactionModeExecuted),
					},
				});
			},
			[agentMessageId, analyticsMessageActions, interactionModeExecuted, trackAIMAUEvent],
		);
		const gatedOnLuminaInteraction = fg('rovo_chat_lumina_component_interaction_tracking')
			? onLuminaInteraction
			: undefined;

		const { registerCopy } = useCopyPasteAdopted();

		const history = useMessageHistory({
			canAttachMessages: true,
			answerId: storeAssistant.content.message.message.id,
		});

		const thumbTriggerRef = useRef<HTMLElement | null>(null);

		const {
			feedbackType: feedbackTypeV2,
			isBannerVisible,
			handleThumbClick: handleV2ThumbClick,
			handleCsatFeedback,
			restartAutoDismiss,
			pauseAutoDismiss,
			csatRating: submittedCsatRating,
			handleProvideAdditionalFeedback,
			handleFeedbackSubmit,
			handleDismissBanner,
			handleIncludeMessagesChecked,
			handleOptIn,
			isOptedIn,
		} = useFeedbackV2({
			entryPointId,
			aaid: aaid ?? 'unknown',
			name,
			email,
			productKey,
			stagingAreaEnabled: elements?.stagingAreaEnabled,
			agentMessageId,
			messageRequestId: storeAssistant.content.metadata?.request_id,
			interactionModeExecuted,
			conversationId,
			selectedAgent,
			history,
			analyticsMessageActions,
			minionAlias: assistanceServiceConfig?.minionAlias,
			channelId: assistanceServiceConfig?.channelId,
			storeUserMessages,
			siteId,
			experienceId,
			productHeader,
			setStoreUserMessages,
			detailedFeedbackSource,
			feedbackTrigger,
		});

		const onAupFeedbackSubmitted = () => {
			setIsAupFeedbackSent(true);
		};

		const feedbackMetadata = {
			aaid: aaid ?? 'unknown',
			answerQuality: 'bad',
			entryPointId,
			assistantResponseId: storeAssistant.content.message.message.id,
			origin: getCurrentOrigin(),
			history,
			feedbackSource: fg('aifc_create_enabled')
				? `${productKey}-v1${elements?.stagingAreaEnabled ? '-staging' : ''}`
				: 'errorMessageFeedback',
			isAupFeedback: true,
		};

		const onThumbClick = (isLike: boolean) => {
			thumbTriggerRef.current = document.activeElement as HTMLElement | null;
			handleV2ThumbClick(isLike);
		};

		const {
			csatRating,
			handleSelectRating,
			handleCsatSubmit,
			handleCsatDismiss,
			isCsatSurveyVisible,
			csatFlowUsedThisTurn,
			recordResolvedLayout,
		} = useStoreAssistantCsatState({
			storeAssistant,
			assistantResponse,
			invokedActions,
			filteredActions,
			feedbackTypeV2,
			streamingComplete,
			shouldHideFooterButtons,
			isConversationActivelyGenerating,
			conversationId: conversationId ?? '',
			agentMessageId,
			handleCsatFeedback,
		});

		const onCopyClick = useCallback(async () => {
			const gateOn = fg('kd_lumina_copy_response_as_adf');
			let node = null;
			if (renderedContentRef.current && streamingComplete) {
				node = renderedContentRef.current;
			}

			// Choose which HTML/plain pair to put in the clipboard. Mid-stream gated
			// content is intentionally plain-text-only so we don't paste a
			// partial/malformed structured payload.
			let clipboardHtml: string | null | undefined;
			let clipboardPlain = plainTextClipboardContent;
			if (gateOn && streamingComplete) {
				const payload = buildClipboardPayload(content, storeMarkdownData);
				clipboardHtml = payload.html;
				clipboardPlain = payload.plain.includes('[^lumina')
					? plainTextClipboardContent
					: payload.plain;
			} else if (!gateOn && node) {
				clipboardHtml = node.innerHTML;
			}

			try {
				if (canWriteRichTextToClipboard(clipboardHtml)) {
					await writeRichTextToClipboard(clipboardHtml, clipboardPlain);
				} else {
					await writePlainTextToClipboard(clipboardPlain);
				}
			} catch {
				await writePlainTextToClipboard(clipboardPlain);
			}

			trackAIMAUEvent({
				type: 'actioned',
				actionResult: 'copyButtonClicked',
				proactive: false,
				messageId: agentMessageId,
				messageActions: analyticsMessageActions,
				singleInstrumentationID: agentMessageId,
				attributes: {
					...interactionModeExecutedAttributes(interactionModeExecuted),
				},
			});

			registerCopy({
				messageId: agentMessageId,
				analyticsMessageActions,
				trackAIMAUEvent,
				interactionModeExecuted,
			});
		}, [
			trackAIMAUEvent,
			agentMessageId,
			analyticsMessageActions,
			renderedContentRef,
			streamingComplete,
			plainTextClipboardContent,
			registerCopy,
			content,
			storeMarkdownData,
			interactionModeExecuted,
		]);

		const onContentSelected = useCallback(() => {
			sendAnalyticsEvent({
				action: 'selected',
				actionSubject: 'content',
				actionSubjectId: 'assistantResponse',
			});
		}, [sendAnalyticsEvent]);

		const onAuthorityPanelShown = useCallback(() => {
			sendAnalyticsEvent(
				buildAuthoritativeAnswerPanelShownEvent({
					product: 'chat',
					correlationAttributes: {
						conversationId,
						messageId: agentMessageId,
					},
					authorityUiMetadata,
				}),
			);
		}, [sendAnalyticsEvent, conversationId, agentMessageId, authorityUiMetadata]);

		// Reuses the shared `sourceLink` event; `clickOrigin` distinguishes these
		// clicks from the regular sources popup, which fires the same event.
		const onAuthorityLinkClicked = useCallback(
			(linkType: 'source' | 'expert') => {
				sendAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'sourceLink',
					attributes: {
						linkType,
						clickOrigin: 'authoritativeAnswerPanel',
						conversationId,
						messageId: agentMessageId,
						...getAuthorityAttributes(authorityUiMetadata),
					},
				});

				// `actioned` credits the click towards Chat's success metric (CSR).
				trackAIMAUEvent({
					type: 'actioned',
					actionResult: 'sourceLinkClicked',
					proactive: false,
					messageId: agentMessageId,
					messageActions: analyticsMessageActions,
					singleInstrumentationID: agentMessageId,
					attributes: {
						clickOrigin: 'authoritativeAnswerPanel',
						linkType,
						...interactionModeExecutedAttributes(interactionModeExecuted),
					},
				});
			},
			[
				sendAnalyticsEvent,
				conversationId,
				agentMessageId,
				authorityUiMetadata,
				trackAIMAUEvent,
				analyticsMessageActions,
				interactionModeExecuted,
			],
		);

		const onContentCopied = useCallback(() => {
			sendAnalyticsEvent({
				action: 'copied',
				actionSubject: 'content',
				actionSubjectId: 'assistantResponse',
			});

			trackAIMAUEvent({
				type: 'actioned',
				actionResult: 'copy',
				proactive: false,
				subjectId: 'assistantResponse',
				messageId: agentMessageId,
				messageActions: analyticsMessageActions,
				singleInstrumentationID: agentMessageId,
				attributes: {
					...interactionModeExecutedAttributes(interactionModeExecuted),
				},
			});

			registerCopy({
				messageId: agentMessageId,
				analyticsMessageActions,
				trackAIMAUEvent,
				interactionModeExecuted,
			});
		}, [
			sendAnalyticsEvent,
			trackAIMAUEvent,
			agentMessageId,
			analyticsMessageActions,
			registerCopy,
			interactionModeExecuted,
		]);

		const onCodeBlockCopied = useCallback(() => {
			sendAnalyticsEvent({
				action: 'copied',
				actionSubject: 'content',
				actionSubjectId: 'assistantResponseCodeBlock',
			});

			trackAIMAUEvent({
				type: 'actioned',
				actionResult: 'codeblockCopied',
				proactive: false,
				subjectId: 'assistantResponseCodeBlock',
				messageId: agentMessageId,
				messageActions: analyticsMessageActions,
				singleInstrumentationID: agentMessageId,
				attributes: {
					...interactionModeExecutedAttributes(interactionModeExecuted),
				},
			});

			registerCopy({
				messageId: agentMessageId,
				analyticsMessageActions,
				trackAIMAUEvent,
				interactionModeExecuted,
			});
		}, [
			sendAnalyticsEvent,
			trackAIMAUEvent,
			agentMessageId,
			analyticsMessageActions,
			registerCopy,
			interactionModeExecuted,
		]);

		const onClickSource = useCallback(
			(_: string, citedProduct: string | null, product: string | null) => {
				trackAIMAUEvent({
					type: 'actioned',
					actionResult: 'sourceLinkClicked',
					proactive: false,
					messageId: agentMessageId,
					messageActions: analyticsMessageActions,
					singleInstrumentationID: agentMessageId,
					citedProduct,
					sourceProduct: product,
					attributes: {
						...interactionModeExecutedAttributes(interactionModeExecuted),
					},
				});
			},
			[trackAIMAUEvent, agentMessageId, analyticsMessageActions, interactionModeExecuted],
		);

		const onClickResponseLink = useCallback(() => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'responseLink',
			});

			trackAIMAUEvent({
				type: 'actioned',
				actionResult: 'responseLinkClicked',
				proactive: false,
				subjectId: 'responseLink',
				messageId: agentMessageId,
				singleInstrumentationID: agentMessageId,
				attributes: {
					...interactionModeExecutedAttributes(interactionModeExecuted),
				},
			});
		}, [trackAIMAUEvent, agentMessageId, sendAnalyticsEvent, interactionModeExecuted]);

		const onClickSmartCard = useCallback(() => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'smartCardLink',
			});

			trackAIMAUEvent({
				type: 'actioned',
				actionResult: 'smartCardClicked',
				proactive: false,
				subjectId: 'smartCardLink',
				singleInstrumentationID: agentMessageId,
				attributes: {
					...interactionModeExecutedAttributes(interactionModeExecuted),
				},
			});
		}, [agentMessageId, sendAnalyticsEvent, trackAIMAUEvent, interactionModeExecuted]);

		const onClickCitation = useCallback(
			(_: number, citedProduct: string | null, product: string | null) => {
				trackAIMAUEvent({
					type: 'actioned',
					actionResult: 'citationClicked',
					proactive: false,
					subjectId: 'citationLink',
					singleInstrumentationID: agentMessageId,
					citedProduct,
					sourceProduct: product,
					attributes: {
						...interactionModeExecutedAttributes(interactionModeExecuted),
					},
				});
			},
			[agentMessageId, trackAIMAUEvent, interactionModeExecuted],
		);

		const onClickInsertReplace = useCallback(
			(mode: 'insert' | 'replace') => {
				trackAIMAUEvent({
					type: 'actioned',
					actionResult: mode,
					subjectId: 'insertReplaceButton',
					proactive: false,
					singleInstrumentationID: agentMessageId,
					attributes: {
						...interactionModeExecutedAttributes(interactionModeExecuted),
					},
				});
			},
			[agentMessageId, trackAIMAUEvent, interactionModeExecuted],
		);

		const onAuthLinkClick = useCallback(
			(url: string, authType: '3p' | 'forge') => {
				setAuth({ url, authType, agentMessageId });

				onAuthLinkClicked?.(authType);
			},
			[onAuthLinkClicked, setAuth, agentMessageId],
		);

		const { fireUiActionedAnalytics, fireAIMAUActionedAnalytics } = useMessageActionedAnalytics();
		const { getActionRenderer } = useActionRenderer();

		const fireAdoptedEvent = useCallback(
			(actionKey: string, actionPayload: MessageActionPayload) => {
				const { getAdoptedObjects } = getActionRenderer(actionKey, [actionPayload.context]);
				if (!getAdoptedObjects) {
					return;
				}
				const rovoModifiedObjects = getAdoptedObjects(actionPayload.context);
				if (rovoModifiedObjects.length) {
					trackAIMAUEvent({
						type: 'adopted',
						actionKey,
						proactive: false,
						rovoModifiedObjects,
						attributes: {
							...interactionModeExecutedAttributes(interactionModeExecuted),
						},
					});
				}
			},
			[getActionRenderer, trackAIMAUEvent, interactionModeExecuted],
		);

		const onActioned = useCallback(
			async <TActionCallbackReturnType = unknown,>(
				payload: MessageActionPayload,
				actionContentProcessor?: ActionContentProcessorFn<TActionCallbackReturnType>,
				options?: { skipSuccessOptimisticAssistantResponse?: boolean },
			): Promise<TActionCallbackReturnType | undefined> => {
				if (!conversationId) {
					return;
				}

				fireUiActionedAnalytics(payload);

				const actionCallbackData = await resolveConversationAction({
					conversationId,
					payload,
					messageId: agentMessageId,
					actionContentProcessor,
					skipSuccessOptimisticAssistantResponse: options?.skipSuccessOptimisticAssistantResponse,
				});

				fireAIMAUActionedAnalytics(payload, agentMessageId, interactionModeExecuted);
				fireAdoptedEvent(payload.context.key, payload);

				return actionCallbackData;
			},
			[
				agentMessageId,
				conversationId,
				resolveConversationAction,
				fireUiActionedAnalytics,
				fireAIMAUActionedAnalytics,
				fireAdoptedEvent,
				interactionModeExecuted,
			],
		);

		const onClickDebug = useCallback(() => {
			if (!conversationId) {
				return;
			}

			openAgentDebugModal({ messageId: agentMessageId, conversationId });

			trackAIMAUEvent({
				type: 'actioned',
				actionResult: 'debugClicked',
				proactive: false,
				singleInstrumentationID: agentMessageId,
				attributes: {
					...interactionModeExecutedAttributes(interactionModeExecuted),
				},
			});
		}, [
			agentMessageId,
			trackAIMAUEvent,
			openAgentDebugModal,
			conversationId,
			interactionModeExecuted,
		]);

		const getOnViewAnalysisClick = useCallback(
			(idx: number) => () => {
				const sculptorData = getMarkdownDataByType({
					markdownData: storeAssistant.markdownData,
					type: 'sculptor',
					index: idx,
				});
				if (sculptorData) {
					openDataSculptorModal(sculptorData);
				}
			},
			[openDataSculptorModal, storeAssistant.markdownData],
		);

		const { redactedResponse } = storeAssistant;
		if (redactedResponse && fg('rovo-guard-ui-redaction-and-blocking')) {
			return (
				<Stack space="space.050">
					<Paper
						showPadding={false}
						variant="neutral"
						pinchCorner="topLeft"
						mode={mode?.toString()}
					>
						<ContentRenderer
							content={redactedResponse}
							productKey={productKey}
							siteId={siteId}
							adfTransformer={injectRedactScribbles}
							extensionComponent={RedactScribbleExtension}
						/>
					</Paper>
					<RedactionEnforcementMessage alignInline="start" />
				</Stack>
			);
		}

		if (!content && !filteredActions?.length && !shouldRenderAgentResponseHat) {
			// Some messages might come empty when an error occurs while waiting for stream response.
			// Still render the response-hat slot when a streamed selected-agent turn is about to
			// produce content, so the text does not shift after the first streamed token arrives.
			return null;
		}

		const sendLinkCountMetric = (streamingCompleted: boolean, adf: DocNode) => {
			if (!streamingCompleted) {
				return;
			}
			const linkCounts = getLinkCounts(adf);
			sendAnalyticsEvent({
				action: 'rendered',
				actionSubject: 'messageContent',
				actionSubjectId: 'linkCounts',
				attributes: {
					totalLinks: linkCounts.totalLinks,
					smartLinkCount: linkCounts.smartLinkCount,
					inlineCardCount: linkCounts.inlineCardCount,
					embedCardCount: linkCounts.embedCardCount,
					regularLinkCount: linkCounts.regularLinkCount,
				},
			});
		};

		const renderedResponseContent = shouldRenderLLMContent ? (
			<Box
				ref={renderedContentRef}
				xcss={shouldEnableAgentResponseHat ? styles.responseContent : undefined}
			>
				<RovoXpcSmartCardProvider mode={mode} fullScreen={fullScreen}>
					<MessageRenderer
						message={assistantResponseMessage}
						onContentSelected={onContentSelected}
						onContentCopied={onContentCopied}
						onCodeBlockCopied={onCodeBlockCopied}
						allowDownloadCodeBlock={true}
						onClickSource={onClickSource}
						onClickResponseLink={onClickResponseLink}
						onClickSmartCard={onClickSmartCard}
						onClickCitation={onClickCitation}
						onAuthLinkClick={onAuthLinkClick}
						getOnViewAnalysisClick={getOnViewAnalysisClick}
						markdownData={storeAssistant.markdownData}
						lightweightEncoding={messageActivelyStreaming}
						contentStillStreaming={!streamingComplete}
						productKey={productKey}
						siteId={siteId}
						orgId={orgId}
						aaid={aaid}
						conversationId={conversation?.id}
						tempIsThirdPartyAgent={conversation?.agent?.creator_type === 'REMOTE_A2A'}
						onDeepLinkClick={customHandlers?.handleDeepLinkClick}
						isMiniModal={mode === ConversationAssistantUIModes.MINI_MODAL}
						isExperimentEnabled={(experimentName: string, needsExposure?: boolean) => {
							return FeatureGates.getExperimentValue(experimentName, 'isEnabled', false, {
								fireExperimentExposure: needsExposure ?? true,
							});
						}}
						adfTransformer={adfSmartlinksPlugin}
						sendLinkCountMetric={sendLinkCountMetric}
						extensionComponent={InsightExtensionWithLayoutContext}
						xpcWrapUrl={xpcWrapUrl}
						onFollowUpClick={onFollowUpClick}
						interactionMode={interactionMode}
						onLuminaInteraction={gatedOnLuminaInteraction}
					/>
				</RovoXpcSmartCardProvider>
			</Box>
		) : null;

		// Navigation + analytics handlers for the source/expert rows are intentionally
		// left unset in this first pass so we can validate the visual treatment. They
		// will be wired up (deep links + GASv3) in a follow-up once the UX is confirmed.
		const isHighlyAuthoritative =
			authorityUiMetadata?.isHighlyAuthoritative ?? authorityUiMetadata?.highlyAuthoritative;

		const renderedAuthorityAttribution =
			streamingComplete &&
			isHighlyAuthoritative &&
			authorityUiMetadata &&
			fg('rovo_authoritative_answer_attribution') ? (
				<RovoXpcSmartCardProvider mode={mode} fullScreen={fullScreen}>
					<AuthoritativeAnswerAttribution
						sources={authorityUiMetadata.sources}
						domainExperts={authorityUiMetadata.domainExperts}
						overallReasoning={authorityUiMetadata.overallReasoning}
						surface="chat"
						// Full screen has ample vertical room, so use the taller panel; the
						// compact panel only makes sense in the narrow sidebar / mini modal.
						panelSize={
							fullScreen || mode === ConversationAssistantUIModes.FULL_SCREEN_MODAL
								? 'tall'
								: 'compact'
						}
						onPanelOpen={onAuthorityPanelShown}
						onSourceClick={() => onAuthorityLinkClicked('source')}
						onExpertClick={() => onAuthorityLinkClicked('expert')}
						onFeedbackSubmit={(submission) => {
							// Submit authority-answer feedback to the Feedback Collector API using
							// Chat's entrypoint (same destination as Chat's collector feedback).
							void submitAuthorityFeedback({
								submission,
								entrypointId: FEEDBACK_ENTRYPOINT_ID_CHAT,
								origin: AUTHORITY_FEEDBACK_ORIGIN_CHAT,
								cloudId: siteId,
							});
						}}
						testId="assistant-authoritative-answer-attribution"
					/>
				</RovoXpcSmartCardProvider>
			) : null;
		const isFromSnapshot = isSharedSnapshotMessage(assistantResponse.message);

		const renderedStreamActions =
			messageType === 'FINAL_RESPONSE' &&
			(fg('aifc_create_enabled') || !isContentPreviewSupported) &&
			!isFromSnapshot ? (
				<MessageStreamActions
					actions={filteredActions ?? []}
					messageId={agentMessageId}
					emojiProvider={emojiProvider}
					cloudId={siteId}
					product={product}
				/>
			) : null;

		const renderedCompletedActions =
			streamingComplete && !isFromSnapshot ? (
				<Box xcss={shouldEnableAgentResponseHat ? undefined : styles.legacyActionsWrapper}>
					{/* Render the static actions */}
					<MessageActions
						interactionModeExecuted={interactionModeExecuted}
						actions={filteredActions ?? []}
						onActioned={onActioned}
						invokedActions={invokedActions}
						recipientAgentNamedId={selectedAgent?.named_id}
						recipientAgentId={selectedAgent?.id}
						messageId={agentMessageId}
						messageType={messageType}
						jiraActivationId={jiraActivationId}
						{...(fg('jira-ai-agentic-planning-technical-changes') && {
							recipientAgentName: selectedAgent?.name,
						})}
						isHistoricalMessage={storeAssistant.isHistorical}
						conversationId={conversationId}
						{...(fg('jira-ai-agentic-planning-technical-changes') && { isLastMessage })}
						{...(fg('jsm-kb-action-message-prop') && {
							isLastActionMessage,
						})}
					/>
					{!shouldHideFooterButtons && (
						<MessageFooterButtons
							hideThumbs={csatFlowUsedThisTurn}
							onThumbClick={onThumbClick}
							onCopyClick={onCopyClick}
							messageId={agentMessageId}
							insertReplaceContent={insertReplaceContent}
							selectedAgentId={selectedAgent?.id}
							onClickInsertReplace={onClickInsertReplace}
							hideInsertButton={hasAutoInsertedContent}
							showDebugButton={
								!selectedAgent?.is_default && fg('rovo_agents_chat_debug_mode_enabled')
							}
							onClickDebug={onClickDebug}
							feedbackType={feedbackTypeV2}
							markdownData={storeAssistant.markdownData}
						/>
					)}
					<VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
						{feedbackTypeV2 && isBannerVisible && formatMessage(feedbackMessages.thankYouMessage)}
					</VisuallyHidden>
					{isCsatSurveyVisible && (
						<Suspense fallback={null}>
							<CsatSurvey
								selectedRating={csatRating}
								onSelectRating={handleSelectRating}
								onSubmit={() => {
									if (csatRating) {
										handleCsatSubmit(csatRating);
									}
								}}
								onClose={handleCsatDismiss}
								onLayoutResolved={recordResolvedLayout}
							/>
						</Suspense>
					)}
					{feedbackTypeV2 && (
						<FeedbackConfirmationBanner
							key={feedbackTypeV2}
							onProvideAdditionalFeedback={handleProvideAdditionalFeedback}
							onDismiss={() => {
								handleDismissBanner();
								thumbTriggerRef.current?.focus();
							}}
							isVisible={isBannerVisible}
							feedbackType={feedbackTypeV2}
							onSubmit={handleFeedbackSubmit}
							onIncludeMessagesChecked={handleIncludeMessagesChecked}
							onOptIn={handleOptIn}
							isOptedIn={isOptedIn}
							hasSeenFeedbackOptInPrompt={hasSeenFeedbackOptInPrompt}
							onOptInPromptActioned={
								setHasSeenFeedbackOptInPrompt
									? () => setHasSeenFeedbackOptInPrompt(true)
									: undefined
							}
							restartAutoDismiss={restartAutoDismiss}
							pauseAutoDismiss={pauseAutoDismiss}
							csatRating={submittedCsatRating}
						/>
					)}
					{shouldShowBetaLabel && <BetaMessageLabel />}
				</Box>
			) : null;

		const renderedSnapshotActions =
			isFromSnapshot && !!filteredActions?.length ? (
				<Box paddingBlockStart="space.100">
					{fg('rovo_chat_readonly_default_action_cards') ? (
						<ReadOnlyDefaultActionCards
							actions={filteredActions}
							ContentRenderer={ContentRenderer}
							productKey={productKey}
							cloudId={siteId}
							conversationId={conversationId}
							messageId={agentMessageId}
						/>
					) : (
						<Box xcss={styles.snapshotActionPlaceholder}>
							<Text color="color.text.disabled">
								{formatMessage(messages.snapshotActionPlaceholder)}
							</Text>
						</Box>
					)}
				</Box>
			) : null;

		return (
			<>
				{renderedAuthorityAttribution}
				<Paper showPadding={false} variant="neutral" pinchCorner="topLeft" mode={mode?.toString()}>
					{shouldEnableAgentResponseHat ? (
						<Stack
							space="space.150"
							xcss={styles.responseCard}
							testId="assistant-response-card-layout"
						>
							{shouldRenderAgentResponseHat && (
								<AgentResponseHat
									isDefaultRovo={isResponseAuthorDefaultRovo}
									agentIdentityAccountId={responseHatAgentIdentityAccountId}
									isIdentityHydrating={isResponseAuthorIdentityHydrating}
									cloudId={siteId}
								/>
							)}
							{renderedResponseContent}
							{renderedStreamActions}
							{renderedCompletedActions}
							{renderedSnapshotActions}
						</Stack>
					) : (
						<>
							{renderedResponseContent}
							{renderedStreamActions}
							{renderedCompletedActions}
							{renderedSnapshotActions}
						</>
					)}
				</Paper>
				<HarmfulContentModal
					name={'Harmful AI Content Feedback'}
					aaid={aaid ?? 'unknown'}
					allowFeedbackMetadata={true}
					feedbackMetadata={feedbackMetadata}
					isOpen={isHarmfulContentModalOpen}
					setIsOpen={setIsHarmfulContentModalOpen}
					onSubmit={onAupFeedbackSubmitted}
				/>
			</>
		);
	},
);
